import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';

export const ROLES = [
  { value: 'Администратор', hint: 'всё, включая пользователей' },
  { value: 'Редактор', hint: 'контингент и журнал, без управления пользователями' },
  { value: 'Читатель', hint: 'только просмотр' },
  { value: 'Преподаватель', hint: 'урезанный список слушателей' },
  { value: 'Преподаватель ЭЖ', hint: 'только свои группы в журнале' },
];

const MIN_PASSWORD = 8;

/**
 * Создание и правка сотрудника.
 *
 * При создании пароль обязателен, при правке — нет: пустое поле оставляет прежний.
 * Сервер при смене пароля или роли завершает все сеансы сотрудника, об этом
 * предупреждаем прямо в форме.
 */
const UserDialog = ({ open, user, onClose, onSubmit }) => {
  const isEdit = Boolean(user);
  const [form, setForm] = useState({ name: '', email: '', role: 'Читатель', password: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit
      ? { name: user.name || '', email: user.email || '', role: user.role, password: '' }
      : { name: '', email: '', role: 'Читатель', password: '' });
  }, [open, isEdit, user]);

  const change = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const passwordTooShort = form.password.length > 0 && form.password.length < MIN_PASSWORD;
  const passwordRequired = !isEdit && form.password.length === 0;
  const roleChanged = isEdit && form.role !== user.role;

  const canSubmit = form.name.trim() && form.email.trim()
    && !passwordTooShort && !passwordRequired;

  const submit = () => {
    const payload = { name: form.name.trim(), email: form.email.trim(), role: form.role };
    if (form.password) payload.password = form.password;

    setSaving(true);
    Promise.resolve(onSubmit(payload)).finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? `Сотрудник: ${user.name}` : 'Новый сотрудник'}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="ФИО" value={form.name} onChange={change('name')} autoFocus />

          <TextField
            label="Логин (email)" value={form.email} onChange={change('email')}
            autoComplete="off"
          />

          <TextField select label="Роль" value={form.role} onChange={change('role')}>
            {ROLES.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                <Stack>
                  <span>{role.value}</span>
                  <span className="journal-muted" style={{ fontSize: 11 }}>{role.hint}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={isEdit ? 'Новый пароль' : 'Пароль'} type="password"
            value={form.password} onChange={change('password')}
            autoComplete="new-password"
            error={passwordTooShort}
            helperText={isEdit
              ? 'Оставьте пустым, чтобы не менять. Не короче 8 символов'
              : 'Не короче 8 символов, не совпадает с логином'}
          />

          {isEdit && (form.password || roleChanged) && (
            <span className="journal-muted">
              После сохранения все сеансы сотрудника завершатся — ему нужно будет войти заново.
            </span>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit || saving}>
          {isEdit ? 'Сохранить' : 'Добавить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDialog;
